import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { ConfigService } from '@core/services/config/config.service';
import { AddIcon } from '@shared/ui/svg-icon/store/svg-icons.actions';
import { getIcon } from '@shared/ui/svg-icon/store/svg-icons.selectors';

@Component({
    selector: 'svg-icon',
    templateUrl: './svg-icon.component.html',
    styleUrls: ['./svg-icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * Componente para mostrar iconos SVG
 */
export class SvgIconComponent implements OnChanges {
    @Input() public src: string;

    @Input() public classes: string;

    @Input() public title: string;

    @Input() public desc: string;

    @Input() public fill: string;

    @Input() public color: string;

    public svg$: Observable<SafeHtml>;

    constructor(
        private readonly http: HttpClient,
        private readonly sanitizer: DomSanitizer,
        private readonly store: Store,
        private readonly configService: ConfigService,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.src.currentValue !== changes.src.previousValue) {
            this.svg$ = this.getIcon(this.src);
        }
    }

    /**
     * Función para obtener un icono desde un JSON externo o desde el servidor
     */
    private getIcon(src: string): Observable<SafeHtml> {
        return this.store.select(getIcon(src))
            .pipe(
                switchMap((icon: string) => {
                    const svgBase64 = this.addContentToSvg(icon);
                    if (!!svgBase64) {
                        return of(this.sanitizer.bypassSecurityTrustHtml(svgBase64));
                    }
                    return this.fetchIcon(src);
                })
            );
    }

    /**
     * Función para obtener un icono desde el servidor
     */
    private fetchIcon(src: string): Observable<SafeHtml> {
        const url = `${this.configService.getConfigUrl<string>('assetsUrl')}/itxwebstandard/icons/${src}.svg`;

        return this.http.get(url, { responseType: 'text' }).pipe(
            take(1),
            map((icon) => {
                const tempParent = this.document.createElement('div');
                tempParent.innerHTML = icon;
                const svg: any = tempParent.querySelector('svg');

                const svgFillSetted = this.traverseAndApplyFill(svg);
                svgFillSetted.setAttribute('class', 'static-icon');
                tempParent.removeChild(svg);
                tempParent.appendChild(svgFillSetted);

                this.store.dispatch(AddIcon({ name: src, icon: tempParent.innerHTML }));
                return this.sanitizer.bypassSecurityTrustHtml(tempParent.innerHTML);
            }),
            catchError(() => {
                console.error(`Error al obtener el icono "${src}" desde el servidor`);
                return of(null);
            })
        );
    }

    private traverseAndApplyFill(node: HTMLElement): HTMLElement {
        const applyFill = (nodeChild: HTMLElement): void => {
            if (nodeChild.nodeType === Node.ELEMENT_NODE && nodeChild.hasAttribute('fill')) {
                const fillValue = nodeChild.getAttribute('fill');
                if (fillValue) {
                    nodeChild.style.setProperty('--fill', fillValue);
                }
            }
            Array.from(nodeChild.children).forEach(child => { applyFill(child as HTMLElement); });
        };

        applyFill(node);
        return node;
    }

    /**
     * Función para añadir contenido al icono SVG
     */
    private addContentToSvg(text: string): string {
        if (this.title || this.desc || this.fill || this.color) {
            const dom = new DOMParser().parseFromString(text, 'text/html');
            const newElement = dom.body.firstElementChild;

            if (this.title) {
                newElement.insertAdjacentHTML('beforeend', `<title>${this.title}</title>`);
            }

            if (this.desc) {
                newElement.insertAdjacentHTML('beforeend', `<desc>${this.desc}</desc>`);
            }

            if (this.fill) {
                newElement.setAttribute('fill', this.fill);
            }

            if (this.color) {
                newElement.querySelector('path').style.fill = this.color;
            }

            return newElement.outerHTML;
        }
        return text;
    }
}
